(() => {
  'use strict';

  const SELECTORS = {
    drawer: 'cart-drawer',
    line: '#CartDrawer .cart-item[data-line-key]',
    quantityButton: '#CartDrawer .quantity__button',
    quantityInput: '#CartDrawer .quantity__input',
    removeButton: '#CartDrawer cart-remove-button',
    checkoutButton: '#CartDrawer-Checkout',
    overlay: '#CartDrawer-Overlay',
    closeButton: '#CartDrawer .drawer__close',
  };

  let updateInProgress = false;

  const getRoot = () => window.Shopify?.routes?.root || '/';

  const getCartChangeUrl = () => {
    if (window.routes?.cart_change_url) return window.routes.cart_change_url;
    return `${getRoot()}cart/change.js`;
  };

  const setDrawerBusy = (busy) => {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    drawer.toggleAttribute('aria-busy', busy);
    drawer.classList.toggle('cart-drawer--updating', busy);

    drawer.querySelectorAll(
      '.quantity__button, .quantity__input, cart-remove-button button, #CartDrawer-Checkout'
    ).forEach((element) => {
      element.disabled = busy;
    });
  };

  const showError = (message, lineElement = null) => {
    const lineError = lineElement?.querySelector('.cart-item__error-text');
    if (lineError) {
      lineError.textContent = message;
      lineError.closest('.cart-item__error')?.classList.remove('hidden');
      return;
    }

    console.error(message);
    window.alert(message);
  };

  const replaceCartIcon = (html) => {
    if (!html) return;

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const renderedSection = documentFragment.querySelector('.shopify-section');
    const currentBubble = document.getElementById('cart-icon-bubble');

    if (currentBubble && renderedSection) {
      currentBubble.innerHTML = renderedSection.innerHTML;
    }
  };

  const replaceDrawer = (html) => {
    if (!html) throw new Error('Shopify returned no cart drawer HTML.');

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const freshDrawer = parsed.querySelector(SELECTORS.drawer);
    const currentDrawer = document.querySelector(SELECTORS.drawer);

    if (!freshDrawer || !currentDrawer) {
      throw new Error('The cart drawer could not be refreshed.');
    }

    const wasOpen = currentDrawer.classList.contains('active');
    currentDrawer.innerHTML = freshDrawer.innerHTML;

    currentDrawer.className = freshDrawer.className;
    if (wasOpen) currentDrawer.classList.add('active');

    document.body.classList.toggle('overflow-hidden', wasOpen);
  };

  const updateCartLine = async (lineElement, quantity) => {
    if (updateInProgress || !lineElement) return;

    const lineKey = lineElement.dataset.lineKey;
    const lineIndex = Number(lineElement.dataset.line);
    const nextQuantity = Math.max(0, Math.trunc(Number(quantity)));

    if ((!lineKey && !lineIndex) || !Number.isFinite(nextQuantity)) return;

    updateInProgress = true;
    setDrawerBusy(true);

    try {
      const payload = {
        quantity: nextQuantity,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: `${window.location.pathname}${window.location.search}`,
      };

      // A line-item key is safest for bundles and products with line-item properties.
      if (lineKey) payload.id = lineKey;
      else payload.line = lineIndex;

      const response = await fetch(getCartChangeUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        throw new Error(
          data.description || data.message || 'Varukorgen kunde inte uppdateras.'
        );
      }

      replaceDrawer(data.sections?.['cart-drawer']);
      replaceCartIcon(data.sections?.['cart-icon-bubble']);

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: data, source: 'purederm-cart-drawer-fix' },
        })
      );
    } catch (error) {
      showError(error.message || 'Varukorgen kunde inte uppdateras.', lineElement);
    } finally {
      updateInProgress = false;
      setDrawerBusy(false);
    }
  };

  const getLineElement = (target) => target.closest(SELECTORS.line);

  // Capture these clicks before the theme's broken/duplicate drawer handlers run.
  window.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const quantityButton = target.closest(SELECTORS.quantityButton);
      if (quantityButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const lineElement = getLineElement(quantityButton);
        const input = lineElement?.querySelector('.quantity__input');
        if (!lineElement || !input) return;

        const current = Number(input.value) || 1;
        const step = Number(input.step) || 1;
        const minimum = Number(input.min) || 0;
        const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;

        let next = current;
        if (quantityButton.name === 'plus') next = Math.min(current + step, maximum);
        if (quantityButton.name === 'minus') next = Math.max(current - step, minimum);

        if (next !== current) {
          input.value = String(next);
          updateCartLine(lineElement, next);
        }
        return;
      }

      const removeButton = target.closest(SELECTORS.removeButton);
      if (removeButton) {
        event.preventDefault();
        event.stopImmediatePropagation();
        updateCartLine(getLineElement(removeButton), 0);
        return;
      }

      const checkoutButton = target.closest(SELECTORS.checkoutButton);
      if (checkoutButton) {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (checkoutButton.disabled || updateInProgress) return;
        checkoutButton.disabled = true;
        window.location.assign('/checkout');
        return;
      }

      const overlay = target.closest(SELECTORS.overlay);
      const closeButton = target.closest(SELECTORS.closeButton);
      if (overlay || closeButton) {
        const drawer = document.querySelector(SELECTORS.drawer);
        if (typeof drawer?.close === 'function') drawer.close();
        else {
          drawer?.classList.remove('active');
          document.body.classList.remove('overflow-hidden');
        }
      }
    },
    true
  );

  window.addEventListener(
    'change',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const input = target?.closest(SELECTORS.quantityInput);
      if (!input) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const lineElement = getLineElement(input);
      const minimum = Number(input.min) || 0;
      const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
      const next = Math.min(
        Math.max(Math.trunc(Number(input.value) || minimum), minimum),
        maximum
      );

      input.value = String(next);
      updateCartLine(lineElement, next);
    },
    true
  );
})();
